'use client';

import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { POPUP_LIST } from '@/app/(publishing)/_config/popup';
import { useBodyScrollLock } from '@publishing/hooks/useBodyScrollLock';
import { useMediaQuery } from '@publishing/hooks/useMediaQuery';
import { PopupCloseContext } from './PopupContent';
import type { PopupConfig, PopupPos } from './types';
import '@publishing/styles/_utilities.scss';
import s from './CommonPopup.module.scss';

const STORAGE_PREFIX = 'popup-dismissed:';

/**
 * pos 선언 시 sticky 스타일 적용
 */
function resolveStickyPos(pos: PopupPos | undefined): CSSProperties | undefined {
  if (!pos) return undefined;
  const rem = (v: number) => `${v / 16}rem`;
  return {
    // top/bottom은 footer에 붙지 않도록 margin-bottom으로 간격 설정
    ...(pos.top !== undefined && { top: rem(pos.top), marginBottom: rem(pos.top) }),
    ...(pos.bottom !== undefined && { bottom: rem(pos.bottom), marginTop: 'auto', marginBottom: rem(pos.bottom) }),
    ...(pos.left !== undefined && { marginLeft: rem(pos.left), marginRight: 'auto' }),
    ...(pos.right !== undefined && { marginRight: rem(pos.right), marginLeft: 'auto' }),
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

function getToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isDismissedToday(id: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${id}`) === getToday();
  } catch {
    return false;
  }
}

function dismissToday(id: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${id}`, getToday());
  } catch {
    // localStorage 사용 불가 환경
  }
}

function matchesPath(pathname: string, pattern: string): boolean {
  if (pattern === '/') return pathname === '/';
  return pathname === pattern || pathname.startsWith(`${pattern}/`);
}

/**
 * 'YYYY-MM-DD' 또는 'YYYY-MM-DD HH:mm' → 타임스탬프. 월·일·시·분 자릿수 무관, 분 생략 가능
 * 게시 기간은 접속자 타임존과 무관하게 KST(+09:00)로 판정한다 — 해외 접속 시 시작/종료 시점이 밀리는 것 방지
 * endOfDay: 시간이 생략된 경우 그날 끝(23:59:59.999)으로 해석 — endDate를 "그날까지"로 읽히게 하기 위함
 */
function toKstTime(value: string, endOfDay = false): number {
  const [y, mo, d, h, mi] = value.trim().split(/[-\s:]+/).map(Number);
  const date = `${y}-${pad(mo)}-${pad(d)}`;
  if (h === undefined && endOfDay) return new Date(`${date}T23:59:59.999+09:00`).getTime();

  return new Date(`${date}T${pad(h ?? 0)}:${pad(mi ?? 0)}:00+09:00`).getTime();
}

function isDateMatch(config: PopupConfig): boolean {
  const now = Date.now();
  if (config.startDate && now < toKstTime(config.startDate)) return false;
  if (config.endDate && now > toKstTime(config.endDate, true)) return false;
  return true;
}

function isPathMatch(pathname: string, config: PopupConfig): boolean {
  if (pathname.includes('/submit')) return false;
  if (config.paths) {
    return config.paths.some((p) => pathname === p);  // 하위 페이지(blog, 상품페이지 등) 팝업 미노출 요청에 의한 처리 260909 jh.roh
  }
  if (config.excludePaths) {
    return !config.excludePaths.some((p) => matchesPath(pathname, p));
  }
  return true;
}

export default function CommonPopup() {
  const pathname = usePathname();
  const isPc = useMediaQuery('(min-width: 1024.02px)');
  const [closedIds, setClosedIds] = useState<Set<string>>(new Set());
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);
  const autoCloseTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    // hydration 후 마운트 상태 동기화
    queueMicrotask(() => setMounted(true));
  }, []);

  useEffect(() => {
    // 경로 변경 시 닫힌 팝업 초기화
    queueMicrotask(() => setClosedIds(new Set()));
  }, [pathname]);

  const visiblePopups = useMemo(() => {
    if (!mounted) return [];
    return POPUP_LIST.filter(
      (p) => isDateMatch(p) && isPathMatch(pathname, p) && !isDismissedToday(p.id) && !closedIds.has(p.id),
    );
  }, [mounted, pathname, closedIds]);

  // pos 있으면 딤드 없음, overlay 명시 시 그 값 우선. 모바일은 항상 딤드
  const showDimmed = visiblePopups.some((p) => (p.overlay ?? !p.pos) || !isPc);

  // pos 팝업만 콘텐츠 영역 안에서 sticky. 위치 기준이 없는 팝업은 기존 fixed 중앙 유지
  const isSticky = !showDimmed && visiblePopups.every((p) => p.pos);

  const handleClose = useCallback((id: string, animate?: boolean) => {
    if (animate) {
      setClosingIds((prev) => new Set(prev).add(id));
      setTimeout(() => {
        setClosingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        setClosedIds((prev) => new Set(prev).add(id));
      }, 400);
    } else {
      setClosedIds((prev) => new Set(prev).add(id));
    }
  }, []);

  const handleDismissToday = useCallback((id: string) => {
    dismissToday(id);
    setClosedIds((prev) => new Set(prev).add(id));
  }, []);

  useEffect(() => {
    const timers = autoCloseTimers.current;
    visiblePopups.forEach((popup) => {
      if (popup.autoCloseMs && !timers.has(popup.id)) {
        const timer = setTimeout(() => {
          handleClose(popup.id, true);
          timers.delete(popup.id);
        }, popup.autoCloseMs);
        timers.set(popup.id, timer);
      }
    });
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [visiblePopups, handleClose]);

  useBodyScrollLock(showDimmed);

  useEffect(() => {
    if (visiblePopups.length === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose(visiblePopups[visiblePopups.length - 1].id);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visiblePopups, handleClose]);

  if (visiblePopups.length === 0) return null;

  return (
    <div className={clsx(s.dialog, !showDimmed && s.pass_through, isSticky && s.sticky_popup)}>
      <div className={s.wrapper}>
        {showDimmed && (
          <div
            className={s.dimmed}
            aria-hidden="true"
            onClick={() => visiblePopups.filter((p) => (p.overlay ?? !p.pos) || !isPc).forEach((p) => handleClose(p.id))}
          />
        )}
        {visiblePopups.map((popup) => {
          const posStyle = isSticky ? resolveStickyPos(popup.pos) : undefined;
          return (
          <PopupCloseContext.Provider key={popup.id} value={() => handleClose(popup.id)}>
            <div id={popup.id} role="dialog" aria-label="팝업" style={posStyle} className={clsx('ff-mkt', s.popup, closingIds.has(popup.id) && s.auto_close)}>
              {popup.content}

              <div className={s.footer}>
                <button type="button" className={s.dismiss} onClick={() => handleDismissToday(popup.id)}>
                  오늘 하루 보지 않기
                </button>
              </div>
            </div>
          </PopupCloseContext.Provider>
          );
        })}
      </div>
    </div>
  );
}

